import React, { useState } from 'react';
import { FileText, AlertTriangle, CheckCircle } from 'lucide-react';

export interface TreatmentInformationData {
  clientJoinedSession: 'on-time' | 'late' | 'early' | 'no-show' | 'cancelled';
  barriers: string;
  behaviorsPresent: boolean;
  behaviorDetails?: string;
  proceduresUsedCorrectly: boolean;
  proceduresFeedback?: string;
  sessionGoals: string[];
  sessionAnecdote: string;
  followUpPlan: string;
}

interface TreatmentInformationProps {
  onComplete: (data: TreatmentInformationData) => void;
  initialData?: Partial<TreatmentInformationData>;
  rbtBehaviorData?: Array<{
    behaviorName: string;
    frequency: number;
    antecedents: string;
    consequences: string;
  }>;
}

const SESSION_GOALS = [
  'Maintain previously mastered skills',
  'Work on new acquisition targets',
  'Reduce challenging behaviors',
  'Practice generalization of skills',
  'Parent/caregiver training',
  'Community-based instruction',
  'Social skills development',
  'Daily living skills',
  'Communication/language goals',
  'Play and leisure skills',
];

export const TreatmentInformation: React.FC<TreatmentInformationProps> = ({
  onComplete,
  initialData,
  rbtBehaviorData,
}) => {
  const [clientJoinedSession, setClientJoinedSession] = useState<TreatmentInformationData['clientJoinedSession']>(
    initialData?.clientJoinedSession || 'on-time'
  );
  const [barriers, setBarriers] = useState(initialData?.barriers || '');
  const [behaviorsPresent, setBehaviorsPresent] = useState(initialData?.behaviorsPresent ?? false);
  const [behaviorDetails, setBehaviorDetails] = useState(initialData?.behaviorDetails || '');
  const [proceduresUsedCorrectly, setProceduresUsedCorrectly] = useState(
    initialData?.proceduresUsedCorrectly ?? true
  );
  const [proceduresFeedback, setProceduresFeedback] = useState(initialData?.proceduresFeedback || '');
  const [sessionGoals, setSessionGoals] = useState<string[]>(initialData?.sessionGoals || []);
  const [sessionAnecdote, setSessionAnecdote] = useState(initialData?.sessionAnecdote || '');
  const [followUpPlan, setFollowUpPlan] = useState(initialData?.followUpPlan || '');
  const [showWarning, setShowWarning] = useState(false);

  const toggleGoal = (goal: string) => {
    setSessionGoals(prev =>
      prev.includes(goal)
        ? prev.filter(g => g !== goal)
        : [...prev, goal]
    );
  };

  const handleSubmit = () => {
    // Validation
    if (behaviorsPresent && !behaviorDetails.trim()) {
      setShowWarning(true);
      return;
    }
    if (!proceduresUsedCorrectly && !proceduresFeedback.trim()) {
      setShowWarning(true);
      return;
    }
    if (!sessionAnecdote.trim()) {
      setShowWarning(true);
      return;
    }

    onComplete({
      clientJoinedSession,
      barriers: barriers.trim(),
      behaviorsPresent,
      behaviorDetails: behaviorsPresent ? behaviorDetails.trim() : undefined,
      proceduresUsedCorrectly,
      proceduresFeedback: !proceduresUsedCorrectly ? proceduresFeedback.trim() : undefined,
      sessionGoals,
      sessionAnecdote: sessionAnecdote.trim(),
      followUpPlan: followUpPlan.trim(),
    });
  };

  const isValid =
    sessionAnecdote.trim() &&
    (!behaviorsPresent || behaviorDetails.trim()) &&
    (proceduresUsedCorrectly || proceduresFeedback.trim());

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      {/* Header */}
      <div style={{
        backgroundColor: '#fef3c7',
        border: '2px solid #f59e0b',
        borderRadius: '8px',
        padding: '16px',
      }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
          <FileText style={{ width: '24px', height: '24px', color: '#d97706', flexShrink: 0, marginTop: '2px' }} />
          <div>
            <h3 style={{ fontSize: '18px', fontWeight: 'bold', color: '#92400e', margin: 0 }}>
              Treatment Information
            </h3>
            <p style={{ fontSize: '14px', color: '#b45309', marginTop: '4px' }}>
              Document treatment details and session observations
            </p>
          </div>
        </div>
      </div>

      {/* Client Joined Session */}
      <div>
        <label style={{ display: 'block', fontSize: '15px', fontWeight: 'bold', color: '#374151', marginBottom: '8px' }}>
          How did the client initially join the session? *
        </label>
        <select
          value={clientJoinedSession}
          onChange={(e) => setClientJoinedSession(e.target.value as TreatmentInformationData['clientJoinedSession'])}
          style={{
            width: '100%',
            padding: '12px',
            border: '2px solid #d1d5db',
            borderRadius: '8px',
            fontSize: '15px',
            fontWeight: '500',
          }}
        >
          <option value="on-time">Arrived on time</option>
          <option value="early">Arrived early</option>
          <option value="late">Arrived late</option>
          <option value="no-show">No show</option>
          <option value="cancelled">Cancelled</option>
        </select>
      </div>

      {/* Barriers */}
      <div>
        <label style={{ display: 'block', fontSize: '15px', fontWeight: 'bold', color: '#374151', marginBottom: '8px' }}>
          List any barriers that are impacting treatment progress at this time
        </label>
        <textarea
          value={barriers}
          onChange={(e) => setBarriers(e.target.value)}
          placeholder="E.g., Client illness, family circumstances, environmental factors, scheduling conflicts..."
          rows={3}
          style={{
            width: '100%',
            padding: '12px',
            border: '2px solid #d1d5db',
            borderRadius: '8px',
            fontSize: '15px',
            resize: 'vertical',
          }}
        />
      </div>

      {/* Behaviors Present */}
      <div style={{
        border: '2px solid #d1d5db',
        borderRadius: '8px',
        padding: '16px',
        backgroundColor: behaviorsPresent ? '#fef2f2' : '#f9fafb',
      }}>
        <label style={{ display: 'block', fontSize: '15px', fontWeight: 'bold', color: '#374151', marginBottom: '12px' }}>
          Behaviors Present During the Session: *
        </label>
        <div style={{ display: 'flex', gap: '12px', marginBottom: behaviorsPresent ? '16px' : '0' }}>
          <button
            onClick={() => { setBehaviorsPresent(true); setShowWarning(false); }}
            style={{
              flex: 1,
              padding: '12px',
              borderRadius: '8px',
              fontSize: '15px',
              fontWeight: '600',
              border: 'none',
              cursor: 'pointer',
              backgroundColor: behaviorsPresent ? '#dc2626' : '#e5e0db',
              color: behaviorsPresent ? 'white' : '#374151',
              transition: 'all 0.2s',
            }}
          >
            Yes - Behavior(s) Present
          </button>
          <button
            onClick={() => { setBehaviorsPresent(false); setShowWarning(false); }}
            style={{
              flex: 1,
              padding: '12px',
              borderRadius: '8px',
              fontSize: '15px',
              fontWeight: '600',
              border: 'none',
              cursor: 'pointer',
              backgroundColor: !behaviorsPresent ? '#16a34a' : '#e5e0db',
              color: !behaviorsPresent ? 'white' : '#374151',
              transition: 'all 0.2s',
            }}
          >
            No Behaviors Present
          </button>
        </div>

        {/* RBT-reported behaviors summary */}
        {behaviorsPresent && rbtBehaviorData && rbtBehaviorData.length > 0 && (
          <div style={{
            backgroundColor: '#fee2e2',
            border: '1px solid #fecaca',
            borderRadius: '6px',
            padding: '12px',
            marginBottom: '12px',
          }}>
            <p style={{ fontSize: '13px', fontWeight: 'bold', color: '#991b1b', marginBottom: '8px' }}>
              RBT-Reported Behaviors:
            </p>
            {rbtBehaviorData.map((behavior, idx) => (
              <div key={idx} style={{ fontSize: '13px', color: '#7f1d1d', marginBottom: '4px' }}>
                • {behavior.behaviorName} (Frequency: {behavior.frequency})
              </div>
            ))}
          </div>
        )}

        {behaviorsPresent && (
          <textarea
            value={behaviorDetails}
            onChange={(e) => { setBehaviorDetails(e.target.value); setShowWarning(false); }}
            placeholder="If behaviors were present, include details about the behavior episode and specific response to behavior/BIP implementation... *"
            rows={4}
            style={{
              width: '100%',
              padding: '12px',
              border: '2px solid #fca5a5',
              borderRadius: '8px',
              fontSize: '15px',
              resize: 'vertical',
              backgroundColor: 'white',
            }}
          />
        )}
      </div>

      {/* Procedures Used */}
      <div style={{
        border: '2px solid #d1d5db',
        borderRadius: '8px',
        padding: '16px',
        backgroundColor: !proceduresUsedCorrectly ? '#fef2f2' : '#f0fdf4',
      }}>
        <label style={{ display: 'block', fontSize: '15px', fontWeight: 'bold', color: '#374151', marginBottom: '12px' }}>
          Procedures Used: *
        </label>
        <div style={{ display: 'flex', gap: '12px', marginBottom: !proceduresUsedCorrectly ? '16px' : '0' }}>
          <button
            onClick={() => { setProceduresUsedCorrectly(true); setShowWarning(false); }}
            style={{
              flex: 1,
              padding: '12px',
              borderRadius: '8px',
              fontSize: '15px',
              fontWeight: '600',
              border: 'none',
              cursor: 'pointer',
              backgroundColor: proceduresUsedCorrectly ? '#16a34a' : '#e5e0db',
              color: proceduresUsedCorrectly ? 'white' : '#374151',
              transition: 'all 0.2s',
            }}
          >
            ABA procedures were used correctly
          </button>
          <button
            onClick={() => { setProceduresUsedCorrectly(false); setShowWarning(false); }}
            style={{
              flex: 1,
              padding: '12px',
              borderRadius: '8px',
              fontSize: '15px',
              fontWeight: '600',
              border: 'none',
              cursor: 'pointer',
              backgroundColor: !proceduresUsedCorrectly ? '#dc2626' : '#e5e0db',
              color: !proceduresUsedCorrectly ? 'white' : '#374151',
              transition: 'all 0.2s',
            }}
          >
            ABA procedures were NOT used correctly
          </button>
        </div>

        {!proceduresUsedCorrectly && (
          <div style={{
            backgroundColor: '#fee2e2',
            border: '1px solid #fecaca',
            borderRadius: '6px',
            padding: '12px',
          }}>
            <p style={{ fontSize: '13px', fontWeight: 'bold', color: '#991b1b', marginBottom: '8px' }}>
              Specify ABA strategies that were implemented. If checked off, procedures were not used correctly - provide feedback given to technician below: *
            </p>
            <textarea
              value={proceduresFeedback}
              onChange={(e) => { setProceduresFeedback(e.target.value); setShowWarning(false); }}
              placeholder="Describe what procedures were not followed correctly and feedback provided..."
              rows={3}
              style={{
                width: '100%',
                padding: '12px',
                border: '2px solid #fca5a5',
                borderRadius: '8px',
                fontSize: '15px',
                resize: 'vertical',
                backgroundColor: 'white',
              }}
            />
          </div>
        )}
      </div>

      {/* Session Goals */}
      <div>
        <label style={{ display: 'block', fontSize: '15px', fontWeight: 'bold', color: '#374151', marginBottom: '8px' }}>
          Select Goals for Progress Report
        </label>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
          gap: '8px',
        }}>
          {SESSION_GOALS.map((goal) => (
            <button
              key={goal}
              onClick={() => toggleGoal(goal)}
              style={{
                padding: '10px 14px',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: '500',
                border: 'none',
                cursor: 'pointer',
                textAlign: 'left',
                backgroundColor: sessionGoals.includes(goal) ? '#4F6367' : '#f3f4f6',
                color: sessionGoals.includes(goal) ? 'white' : '#374151',
                transition: 'all 0.2s',
              }}
            >
              {goal}
            </button>
          ))}
        </div>
      </div>

      {/* Session Anecdote */}
      <div>
        <label style={{ display: 'block', fontSize: '15px', fontWeight: 'bold', color: '#374151', marginBottom: '8px' }}>
          Session Anecdote and Follow Up Plan: *
        </label>
        <p style={{ fontSize: '13px', color: '#6b6b6b', marginBottom: '8px' }}>
          Use this area to describe the session summary as an anecdote. What activities or task were done while implementing the targets? What was the client's response? Was progress noted with individual goals? Any specific changes to the teaching techniques? Include any modifications to treatment, additional goals or trainings that will be added as a result of this session. If no specific follow up plan is needed, write "Continue to implement treatment as outlined in client's treatment program."
        </p>
        <textarea
          value={sessionAnecdote}
          onChange={(e) => { setSessionAnecdote(e.target.value); setShowWarning(false); }}
          placeholder="Describe the session as an anecdote with client progress and teaching techniques used..."
          rows={6}
          style={{
            width: '100%',
            padding: '12px',
            border: '2px solid #d1d5db',
            borderRadius: '8px',
            fontSize: '15px',
            resize: 'vertical',
          }}
        />
      </div>

      {/* Follow Up Plan */}
      <div>
        <label style={{ display: 'block', fontSize: '15px', fontWeight: 'bold', color: '#374151', marginBottom: '8px' }}>
          Specific Follow Up Plan (if any)
        </label>
        <textarea
          value={followUpPlan}
          onChange={(e) => setFollowUpPlan(e.target.value)}
          placeholder="Enter any specific follow-up actions needed, or leave blank if continuing standard treatment..."
          rows={3}
          style={{
            width: '100%',
            padding: '12px',
            border: '2px solid #d1d5db',
            borderRadius: '8px',
            fontSize: '15px',
            resize: 'vertical',
          }}
        />
      </div>

      {/* Warning Message */}
      {showWarning && (
        <div style={{
          backgroundColor: '#fef2f2',
          border: '2px solid #fca5a5',
          borderRadius: '8px',
          padding: '16px',
        }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
            <AlertTriangle style={{ width: '20px', height: '20px', color: '#dc2626', flexShrink: 0, marginTop: '2px' }} />
            <div>
              <p style={{ fontSize: '14px', fontWeight: 'bold', color: '#991b1b', marginBottom: '4px' }}>
                Cannot Submit - Missing Required Information
              </p>
              <ul style={{ fontSize: '13px', color: '#7f1d1d', margin: 0, paddingLeft: '16px' }}>
                {!sessionAnecdote.trim() && <li>Session anecdote is required</li>}
                {behaviorsPresent && !behaviorDetails.trim() && <li>Behavior details are required when behaviors are present</li>}
                {!proceduresUsedCorrectly && !proceduresFeedback.trim() && <li>Feedback is required when procedures were not used correctly</li>}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Submit Button */}
      <button
        onClick={handleSubmit}
        disabled={!isValid}
        style={{
          width: '100%',
          padding: '16px',
          borderRadius: '8px',
          fontSize: '16px',
          fontWeight: 'bold',
          border: 'none',
          cursor: isValid ? 'pointer' : 'not-allowed',
          backgroundColor: isValid ? '#d97706' : '#d1d5db',
          color: isValid ? 'white' : '#6b6b6b',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          transition: 'all 0.2s',
        }}
      >
        <CheckCircle style={{ width: '20px', height: '20px' }} />
        Complete Treatment Information
      </button>

      {/* Info */}
      <div style={{
        backgroundColor: '#f9fafb',
        borderRadius: '8px',
        padding: '12px',
        fontSize: '13px',
        color: '#6b6b6b',
      }}>
        <p style={{ margin: 0, marginBottom: '4px' }}>• Required fields are marked with *</p>
        <p style={{ margin: 0, marginBottom: '4px' }}>• Session anecdote should describe activities, client responses, and progress</p>
        <p style={{ margin: 0 }}>• Include behavior details and procedure feedback when applicable</p>
      </div>
    </div>
  );
};
